/**
 * combat/buffs.ts — The Weight of Blessings and Curses
 *
 * Every buff is a promise. Every debuff is a debt. This system tracks them,
 * ticks them, stacks them, and cleans them up when their time is done.
 *
 * Buffs modify combat stats in real-time. DoTs tick damage. HoTs tick healing.
 * Shields absorb hits. Stuns lock you in place. Everything has a duration,
 * everything has a visual, and nothing lingers silently.
 */

import type { GameState, ActiveBuff, BuffType, DamageType } from "../engine/state.js";

// ─── Audio Engine Reference ──────────────────────────────────────────────
// Set once from the game loop to enable buff gain/fade sounds.

let audioEngine: { playInteraction(id: string): void } | null = null;

export function setBuffAudioEngine(engine: { playInteraction(id: string): void } | null): void {
  audioEngine = engine;
}

// ─── Buff Metadata ────────────────────────────────────────────────────────

export interface BuffMeta {
  name: string;
  icon: string;
  color: string;          // primary UI color
  glowColor: string;      // particle/aura color
  isDebuff: boolean;
  /** Damage type for DoT ticks */
  tickDamageType: DamageType | null;
  /** Can this buff type be dispelled? */
  dispellable: boolean;
}

export const BUFF_META: Record<BuffType, BuffMeta> = {
  shield:      { name: "Shield",       icon: "🛡️", color: "#88BBDD", glowColor: "#AADDFF", isDebuff: false, tickDamageType: null, dispellable: true },
  regen:       { name: "Regeneration", icon: "💚", color: "#44CC44", glowColor: "#88FF88", isDebuff: false, tickDamageType: null, dispellable: true },
  haste:       { name: "Haste",        icon: "⚡", color: "#FFDD44", glowColor: "#FFEE88", isDebuff: false, tickDamageType: null, dispellable: true },
  might:       { name: "Might",        icon: "💪", color: "#DD6644", glowColor: "#FF8866", isDebuff: false, tickDamageType: null, dispellable: true },
  fortify:     { name: "Fortify",      icon: "🏰", color: "#8888AA", glowColor: "#AAAACC", isDebuff: false, tickDamageType: null, dispellable: true },
  thorns:      { name: "Thorns",       icon: "🌿", color: "#44AA33", glowColor: "#66DD44", isDebuff: false, tickDamageType: null, dispellable: true },
  burn:        { name: "Burn",         icon: "🔥", color: "#FF4400", glowColor: "#FF6622", isDebuff: true, tickDamageType: "fire", dispellable: true },
  poison:      { name: "Poison",       icon: "☠️", color: "#66AA22", glowColor: "#88CC44", isDebuff: true, tickDamageType: "nature", dispellable: true },
  slow:        { name: "Slow",         icon: "🐢", color: "#6688BB", glowColor: "#88AADD", isDebuff: true, tickDamageType: null, dispellable: true },
  frostbite:   { name: "Frostbite",    icon: "❄️", color: "#66AAFF", glowColor: "#88CCFF", isDebuff: true, tickDamageType: "frost", dispellable: true },
  shadow_mark: { name: "Shadow Mark",  icon: "👁️", color: "#8855CC", glowColor: "#AA77EE", isDebuff: true, tickDamageType: null, dispellable: true },
  stun:        { name: "Stun",         icon: "💫", color: "#CCCC44", glowColor: "#EEEE66", isDebuff: true, tickDamageType: null, dispellable: false },
};

// ─── Unique ID Generator ──────────────────────────────────────────────────

let buffIdCounter = 0;
function nextBuffId(): string {
  return `buff_${++buffIdCounter}`;
}

// ─── Apply Buff ───────────────────────────────────────────────────────────

/**
 * Apply a buff/debuff to the player. Handles stacking, refreshing, and
 * capping. Returns the applied buff for chaining.
 */
export function applyBuff(
  state: GameState,
  type: BuffType,
  value: number,
  duration: number,
  tickInterval: number,
  maxStacks: number,
  sourceAbilityId: string | null,
): ActiveBuff {
  const meta = BUFF_META[type];
  const now = performance.now() / 1000;
  const buffs = state.player.activeBuffs;

  // Check for existing buff of same type from same source
  const existing = buffs.find(b => b.type === type && b.sourceAbilityId === sourceAbilityId);

  if (existing) {
    // Stack or refresh
    if (existing.stacks < existing.maxStacks) {
      existing.stacks++;
    }
    // Always refresh duration
    existing.expiresAt = now + duration;
    existing.value = value; // update value in case it scaled differently
    return existing;
  }

  // New buff
  const buff: ActiveBuff = {
    id: nextBuffId(),
    type,
    name: meta.name,
    icon: meta.icon,
    stacks: 1,
    maxStacks,
    expiresAt: now + duration,
    tickInterval,
    lastTickAt: now,
    value,
    sourceAbilityId,
    isDebuff: meta.isDebuff,
  };

  buffs.push(buff);
  // Buff gain sound — magical shimmer
  audioEngine?.playInteraction?.("buff_gain");
  return buff;
}

// ─── Remove Buff ──────────────────────────────────────────────────────────

/** Remove a specific buff by ID */
export function removeBuff(state: GameState, buffId: string): void {
  state.player.activeBuffs = state.player.activeBuffs.filter(b => b.id !== buffId);
}

/** Remove all buffs of a given type */
export function removeBuffsByType(state: GameState, type: BuffType): void {
  state.player.activeBuffs = state.player.activeBuffs.filter(b => b.type !== type);
}

/** Remove all debuffs (dispel) */
export function dispelDebuffs(state: GameState): void {
  state.player.activeBuffs = state.player.activeBuffs.filter(
    b => !b.isDebuff || !BUFF_META[b.type].dispellable,
  );
}

/** Remove all buffs (for death / zone change) */
export function clearAllBuffs(state: GameState): void {
  state.player.activeBuffs = [];
}

// ─── Tick Processing ──────────────────────────────────────────────────────

/**
 * Process all active buffs: tick DoTs/HoTs, expire finished buffs.
 * Returns total damage taken this tick (for damage number spawning)
 * and total healing received.
 */
export interface BuffTickResult {
  totalDotDamage: number;
  totalHotHealing: number;
  /** Individual tick events for spawning damage numbers */
  ticks: Array<{
    buffType: BuffType;
    value: number;
    damageType: DamageType;
    isHeal: boolean;
  }>;
  /** Buffs that just expired (for fade-out visuals) */
  expired: ActiveBuff[];
}

export function tickBuffs(state: GameState, dt: number): BuffTickResult {
  const now = performance.now() / 1000;
  const result: BuffTickResult = {
    totalDotDamage: 0,
    totalHotHealing: 0,
    ticks: [],
    expired: [],
  };

  const surviving: ActiveBuff[] = [];

  for (const buff of state.player.activeBuffs) {
    // Check expiry
    if (now >= buff.expiresAt) {
      result.expired.push(buff);
      // Buff fade sound — soft dissolve
      audioEngine?.playInteraction?.("buff_fade");
      continue;
    }

    // Process ticks for DoTs and HoTs
    if (buff.tickInterval > 0 && now - buff.lastTickAt >= buff.tickInterval) {
      buff.lastTickAt = now;
      const tickValue = buff.value * buff.stacks;
      const meta = BUFF_META[buff.type];

      if (meta.tickDamageType && buff.isDebuff) {
        // DoT tick — damage the player
        const dmg = Math.max(1, Math.round(tickValue));
        state.player.combat.hp -= dmg;
        result.totalDotDamage += dmg;
        result.ticks.push({
          buffType: buff.type,
          value: dmg,
          damageType: meta.tickDamageType,
          isHeal: false,
        });
      } else if (buff.type === "regen" || (!buff.isDebuff && buff.tickInterval > 0)) {
        // HoT tick — heal the player
        const healAmt = Math.max(1, Math.round(tickValue));
        state.player.combat.hp = Math.min(
          state.player.combat.hp + healAmt,
          state.player.combat.maxHp,
        );
        result.totalHotHealing += healAmt;
        result.ticks.push({
          buffType: buff.type,
          value: healAmt,
          damageType: "heal",
          isHeal: true,
        });
      }
    }

    surviving.push(buff);
  }

  state.player.activeBuffs = surviving;
  return result;
}

// ─── Stat Modifiers from Active Buffs ─────────────────────────────────────

export interface BuffStatModifiers {
  attackBonus: number;
  defenseBonus: number;
  attackSpeedMultiplier: number;
  damageReduction: number;      // 0.0 - 1.0
  thornsReflect: number;        // damage reflected per hit
  shieldHp: number;             // total shield absorption remaining
  isStunned: boolean;
  isSlowed: boolean;
  damageTakenMultiplier: number;  // >1.0 means increased damage taken
}

/** Calculate aggregate stat modifiers from all active buffs */
export function getBuffModifiers(state: GameState): BuffStatModifiers {
  const mods: BuffStatModifiers = {
    attackBonus: 0,
    defenseBonus: 0,
    attackSpeedMultiplier: 1.0,
    damageReduction: 0,
    thornsReflect: 0,
    shieldHp: 0,
    isStunned: false,
    isSlowed: false,
    damageTakenMultiplier: 1.0,
  };

  for (const buff of state.player.activeBuffs) {
    const v = buff.value * buff.stacks;

    switch (buff.type) {
      case "might":
        mods.attackBonus += v;
        break;
      case "fortify":
        mods.defenseBonus += v;
        mods.damageReduction = Math.min(0.75, mods.damageReduction + v / 100);
        break;
      case "haste":
        mods.attackSpeedMultiplier *= 1 + v / 100;
        break;
      case "shield":
        mods.shieldHp += v;
        break;
      case "thorns":
        mods.thornsReflect += v;
        break;
      case "stun":
        mods.isStunned = true;
        break;
      case "slow":
        mods.attackSpeedMultiplier *= 0.6;
        mods.isSlowed = true;
        break;
      case "shadow_mark":
        mods.damageTakenMultiplier *= 1.15;
        break;
      // DoT types (burn, poison, frostbite) are handled by tick processing
      case "frostbite":
        mods.attackSpeedMultiplier *= 0.8;
        mods.isSlowed = true;
        break;
    }
  }

  return mods;
}

// ─── Shield Absorption ────────────────────────────────────────────────────

/**
 * Absorb incoming damage through shields. Returns remaining damage
 * after shields are consumed.
 */
export function absorbDamage(state: GameState, incomingDamage: number): number {
  let remaining = incomingDamage;

  for (const buff of state.player.activeBuffs) {
    if (buff.type !== "shield" || remaining <= 0) continue;

    const absorbed = Math.min(buff.value, remaining);
    buff.value -= absorbed;
    remaining -= absorbed;

    // Shield depleted — remove it
    if (buff.value <= 0) {
      buff.expiresAt = 0; // will be cleaned up next tick
    }
  }

  return remaining;
}

// ─── Thorns Calculation ───────────────────────────────────────────────────

/** Calculate thorns damage to reflect back to attacker */
export function calcThornsDamage(state: GameState): number {
  let total = 0;
  for (const buff of state.player.activeBuffs) {
    if (buff.type === "thorns") {
      total += buff.value * buff.stacks;
    }
  }
  return Math.round(total);
}

// ─── Convenience Queries ──────────────────────────────────────────────────

export function hasBuffType(state: GameState, type: BuffType): boolean {
  return state.player.activeBuffs.some(b => b.type === type);
}

export function getBuffStacks(state: GameState, type: BuffType): number {
  const buff = state.player.activeBuffs.find(b => b.type === type);
  return buff ? buff.stacks : 0;
}

export function getBuffTimeRemaining(state: GameState, type: BuffType): number {
  const buff = state.player.activeBuffs.find(b => b.type === type);
  if (!buff) return 0;
  return Math.max(0, buff.expiresAt - performance.now() / 1000);
}

/** Count total active buffs (beneficial) */
export function countBuffs(state: GameState): number {
  return state.player.activeBuffs.filter(b => !b.isDebuff).length;
}

/** Count total active debuffs */
export function countDebuffs(state: GameState): number {
  return state.player.activeBuffs.filter(b => b.isDebuff).length;
}
